import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CommonIcons } from 'common-src/assets/Icons';
import { Button, RequestHandlerScreen } from 'common-src/components/base';
import {
  createZusPatientFromAthena,
  deletePatientIntegrationsZus,
  getPatientIntegrationsZus,
  getPatientIntegrationsZusRequest,
  refreshZusPatientHistoryPullStatus,
  startZusPatientHistoryPull,
  syncPatientIntegrationsZus,
  triggerZusPatientSetupWorkflow,
} from 'common-src/models/PatientIntegrationsZus';
import { Colors, ColorsNew } from 'common-src/styles';

import { ActionsRow, BaseTable, TablePagination, TextItem } from 'src/components/table';
import { ResultsCount } from 'src/components/table/TablePagination/constants';
import { TabsViewNew } from 'src/components/tabs';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useInterval from 'src/hooks/useInterval';
import useRequestLoading from 'src/hooks/useRequestLoading';

import styles from '../Athena/Athena.module.scss';
import { DefaultTabInfo, TableHeaders, ZusTabs } from './constants';
import { getTabInfo } from './helpers';

const formatDate = (value) => (value ? moment(value).format('MM/DD/YYYY HH:mm') : '-');

const Zus = ({ patientId }) => {
  const dispatch = useDispatch();

  const tabs = Object.keys(ZusTabs);

  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [tabInfo, setTabInfo] = useState(DefaultTabInfo);
  const [filteredIntegrations, setFilteredIntegrations] = useState([]);
  const [paginationData, setPaginationData] = useState({ from: 0, to: undefined });

  const integrations = useCustomSelector((state) => getPatientIntegrationsZus(state, patientId));
  const { showAlert, AlertType } = useAlert();
  const { isRequesting } = useRequestLoading([getPatientIntegrationsZusRequest({ patientId })]);

  const refreshIntegrations = () => {
    dispatch(getPatientIntegrationsZusRequest({ patientId, sort_order: 'desc', limit: 5 }, {}));
  };

  useInterval(refreshIntegrations);

  useEffect(() => {
    const { tabInfo, activeIntegrations, inactiveIntegrations, allIntegrations } =
      getTabInfo(integrations);
    setTabInfo(tabInfo);

    switch (selectedTab) {
      case tabs[0]:
        setFilteredIntegrations(activeIntegrations);
        break;
      case tabs[1]:
        setFilteredIntegrations(inactiveIntegrations);
        break;
      default:
        setFilteredIntegrations(allIntegrations);
        break;
    }
  }, [selectedTab, integrations]);

  const getData = () =>
    filteredIntegrations.map((integration) => ({
      zusPatientId: <TextItem isActive text={integration.zusPatientId} />,
      zusUpid: <TextItem isActive text={integration.zusUpid || '-'} />,
      status: <TextItem isActive text={integration.status || '-'} />,
      historyJob: (
        <TextItem
          isActive
          text={integration.lastHistoryJobStatus || '-'}
          value={integration.lastHistoryJobId || ''}
        />
      ),
      lastSyncedAt: (
        <TextItem
          isActive
          text={formatDate(integration.lastSyncedAt)}
          value={integration.lastSyncedAt}
        />
      ),
      actions: (
        <ActionsRow
          options={[
            {
              id: 'start-history-button',
              text: 'History',
              backgroundColor: '#4A90E2CC',
              color: 'white',
              disabled: !!integration.deletedAt,
              onClick: () =>
                dispatch(
                  startZusPatientHistoryPull(
                    patientId,
                    {},
                    {
                      successBlock: () => {
                        showAlert(AlertType.Success, '', 'ZUS history job started');
                        refreshIntegrations();
                      },
                      errorBlock: (err) => showAlert(AlertType.Error, '', err),
                    },
                  ),
                ),
            },
            {
              id: 'refresh-history-button',
              text: 'Refresh',
              backgroundColor: '#375844CC',
              color: 'white',
              disabled: !!integration.deletedAt || !integration.lastHistoryJobId,
              onClick: () =>
                dispatch(
                  refreshZusPatientHistoryPullStatus(patientId, {
                    successBlock: () => {
                      showAlert(AlertType.Success, '', 'ZUS history status refreshed');
                      refreshIntegrations();
                    },
                    errorBlock: (err) => showAlert(AlertType.Error, '', err),
                  }),
                ),
            },
            {
              id: 'mark-as-inactive-button',
              text: 'Inactive',
              backgroundColor: '#F01F51B2',
              color: 'white',
              disabled: !!integration.deletedAt,
              showWarning: true,
              onClick: () =>
                dispatch(
                  deletePatientIntegrationsZus(integration.id, {
                    successBlock: () => {
                      showAlert(AlertType.Success, '', 'ZUS integration deleted');
                      refreshIntegrations();
                    },
                    errorBlock: (err) => showAlert(AlertType.Error, '', err),
                  }),
                ),
            },
          ]}
        />
      ),
    }));

  if (isRequesting) {
    return <RequestHandlerScreen isRequesting requestingText="Pulling ZUS integrations..." />;
  }

  return (
    <>
      <TabsViewNew
        classNames={['medical-tabs']}
        tabClassNames={['flex-none']}
        options={tabInfo}
        onTabSelected={setSelectedTab}
        showZero
        rightContent={
          <>
            <Button
              id="trigger-zus-patient-setup-workflow-button"
              text="Trigger ZUS Workflow"
              onClick={() =>
                dispatch(
                  triggerZusPatientSetupWorkflow(patientId, {
                    successBlock: () => {
                      showAlert(AlertType.Success, '', 'ZUS workflow triggered');
                      refreshIntegrations();
                    },
                    errorBlock: (err) => showAlert(AlertType.Error, '', err),
                  }),
                )
              }
              backgroundColor={Colors.white}
              textColor={ColorsNew.darkGreen}
              iconSrc={CommonIcons.plusIcon}
              classNames={[styles.button]}
            />
            <Button
              id="create-zus-patient-button"
              text="Create ZUS Patient"
              onClick={() =>
                dispatch(
                  createZusPatientFromAthena(patientId, {
                    successBlock: () => {
                      showAlert(AlertType.Success, '', 'ZUS patient created');
                      refreshIntegrations();
                    },
                    errorBlock: (err) => showAlert(AlertType.Error, '', err),
                  }),
                )
              }
              backgroundColor={Colors.white}
              textColor={ColorsNew.darkGreen}
              iconSrc={CommonIcons.plusIcon}
              classNames={[styles.button]}
            />
            <Button
              id="sync-zus-patient-button"
              text="Sync ZUS Patient"
              onClick={() =>
                dispatch(
                  syncPatientIntegrationsZus(patientId, {
                    successBlock: () => {
                      showAlert(AlertType.Success, '', 'ZUS patient synced');
                      refreshIntegrations();
                    },
                    errorBlock: (err) => showAlert(AlertType.Error, '', err),
                  }),
                )
              }
              backgroundColor={Colors.white}
              textColor={ColorsNew.darkGreen}
              iconSrc={CommonIcons.plusIcon}
              classNames={[styles.button]}
            />
          </>
        }
      />
      <BaseTable
        headers={TableHeaders}
        data={getData()}
        name="zus-integrations"
        classNames={[
          'medical-table-new',
          filteredIntegrations?.length <= ResultsCount[25] ? 'no-pagination' : '',
        ]}
        emptyText="No ZUS integrations"
        emptyClassNames={['medical-table-empty']}
        initialSortColumn={{
          column: 'lastSyncedAt',
          label: 'Last Synced',
          width: '14%',
          sortBy: 'value',
        }}
        paginationData={paginationData}
      />
      <TablePagination onChangePageHandler={setPaginationData} results={filteredIntegrations} />
    </>
  );
};

Zus.propTypes = {
  patientId: PropTypes.number,
};

export default Zus;
